import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChildFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';

import { AuthGuardData, createAuthGuard } from 'keycloak-angular';

import { MeApiService } from '@core/data/api/me-api.service';

import { APP_RUNTIME_CONFIG } from './app-config.model';
import { AuthService } from './auth.service';

/**
 * Real authentication + authorization gate, wrapped around the whole app shell.
 *
 * 1. Authentication — with `onLoad: 'login-required'` the user is already sent to
 *    Keycloak during init, so this is a belt-and-suspenders check; if somehow
 *    unauthenticated, it triggers the login redirect.
 * 2. Authorization — enforces the configured `requiredRoles`. A user who is
 *    authenticated but holds none of the DROS realm roles (i.e. not authorized to
 *    use this app) is routed to /access-denied instead of the shell. Leaving
 *    requiredRoles empty admits any authenticated realm user.
 * 3. Registration — the API now also refuses anybody it has no record of, with a
 *    403 on EVERY call (`backend-contract-signing-in.md` §0). Left alone that is a
 *    blank screen with no explanation, so the same question is asked here, once,
 *    at the door: `GET /api/v1/me` answers even for somebody otherwise refused,
 *    and a null `personaCode` is the tell. Asking here rather than reacting to
 *    403s means they land on a page that explains it instead of watching the app
 *    fail to load.
 * 4. Authorization, per surface — `GET /api/v1/me/access` says what this caller
 *    may open, and is loaded here so the sidebar and `accessGuard` can read it
 *    synchronously while they render.
 *
 * This is distinct from `accessGuard` (in-app role→route authorization driven by
 * the Users & Access matrix + persona), which still runs alongside it.
 */
const isAllowed = async (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  authData: AuthGuardData,
): Promise<boolean | UrlTree> => {
  const { authenticated, grantedRoles } = authData;
  // Every inject() runs before the first await — afterwards there is no injection
  // context to run it in.
  const router = inject(Router);
  const config = inject(APP_RUNTIME_CONFIG);
  const me = inject(MeApiService);
  const auth = inject(AuthService);

  if (!authenticated) {
    // Straight to Keycloak. With `login-required` init has normally done this
    // already, so this is the belt-and-braces case — a session that died between
    // init and a navigation. Either way the redirect happens because somebody just
    // navigated, so the sign-in page it opens is as fresh as the click.
    void auth.login(state.url);
    return false;
  }

  // Membership of ANY of the DROS realm roles is what grants entry. Checking for a
  // single coarse role was wrong against the real realm: `dros-user` does not exist,
  // so every genuine user was sent to /access-denied.
  const required = (config.keycloak.requiredRoles ?? []).filter((r) => r.trim());
  if (required.length) {
    const granted = new Set([
      ...grantedRoles.realmRoles,
      ...Object.values(grantedRoles.resourceRoles).flat(),
    ]);
    if (!required.some((role) => granted.has(role))) {
      auth.refusal.set('no-role');
      return router.createUrlTree(['/access-denied']);
    }
  }

  // Null = we could not ask (no API configured, or the call failed). That is not a
  // refusal: locking somebody out of an app that worked a minute ago because a
  // request failed would be worse than letting the API refuse them itself.
  const caller = await me.current();
  if (caller) {
    if (caller.accountStatus === 'SUSPENDED') {
      auth.refusal.set('suspended');
      return router.createUrlTree(['/access-denied']);
    }
    if (!caller.personaCode) {
      // AN UNVERIFIED ADDRESS LOOKS EXACTLY LIKE NOT BEING SET UP, and it is the
      // likelier of the two for a first administrator: the server matches an
      // account BY EMAIL and refuses an unverified one — otherwise anybody able
      // to set an email claim could adopt somebody else's account — and Keycloak
      // leaves the flag off for a hand-created user. Checked FIRST because the
      // account may well exist: sending somebody to an administrator to be added
      // again would be the wrong errand entirely.
      if (!auth.identity()?.emailVerified) {
        auth.refusal.set('email-unverified');
        return router.createUrlTree(['/access-denied']);
      }
      // `roles` empty means DROS has recorded nothing for them at all; a role with
      // no persona is the rarer half-created case, and an administrator fixes it by
      // linking them rather than by adding them.
      auth.refusal.set(caller.roles.length ? 'no-person' : 'not-set-up');
      return router.createUrlTree(['/access-denied']);
    }
  }

  auth.refusal.set(null);
  // Load what they may do before navigation decides anything. accessGuard runs
  // after this one and reads it synchronously, so awaiting here is what stops the
  // first route being judged against an empty answer and bouncing to Today.
  await me.access();
  return true;
};

export const authnGuard: CanActivateChildFn = createAuthGuard<CanActivateChildFn>(isAllowed);
